import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Search, Megaphone, MousePointerClick, LayoutGrid, Users, Sparkles } from "lucide-react";
import MarqueeBand from "@/components/MarqueeBand";
import { FadeUp, MaskedLine, DrawBorder } from "@/components/Reveal";
import { openBooking } from "@/lib/api";

const PALM =
  "https://images.pexels.com/photos/27394932/pexels-photo-27394932.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OFFICE =
  "https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SAND =
  "https://images.pexels.com/photos/952670/pexels-photo-952670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  { icon: Search, title: "SEO", text: "Own page one. Technical audits, content strategy, and local search that puts Florida businesses on the map.", span: "md:col-span-2" },
  { icon: Megaphone, title: "Social Media", text: "Feeds people actually stop for. Strategy, content, and community management.", span: "" },
  { icon: MousePointerClick, title: "Paid Advertising", text: "Google, Meta, and beyond. Every dollar tracked, every click accountable.", span: "" },
  { icon: LayoutGrid, title: "Web Design", text: "Fast, conversion-obsessed sites built to turn visitors into revenue.", span: "" },
  { icon: Users, title: "Lead Generation", text: "Pipelines that never sleep. Landing pages, funnels, and nurture systems.", span: "" },
  { icon: Sparkles, title: "+ More", text: "Content, email, branding, analytics. One crew, full stack.", span: "", lime: true },
];

const chapters = [
  { num: "01", title: "We crawl so you climb", text: "Ants map every inch of terrain before they move. We do the same with your market — research first, then relentless execution." },
  { num: "02", title: "Small crew, colony output", text: "A single ant is unimpressive. A colony moves mountains. Our tight senior team punches absurdly above its weight." },
  { num: "03", title: "Data over guesswork", text: "Every trail we lay is measured. If a channel doesn't pay, we reroute the colony. No vanity metrics, ever." },
  { num: "04", title: "Aloha is our SLA", text: "Based in Fort Pierce, wired for hustle. Real humans, same-day answers, and partnerships that outlast campaigns." },
];

const stats = [
  { value: "+214%", label: "Avg. organic traffic lift" },
  { value: "38", label: "Brands in the colony" },
  { value: "4.9/5", label: "Client rating" },
  { value: "12hrs", label: "Avg. response time" },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yPalm = useTransform(scrollY, [0, 700], [0, 140]);
  const yText = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <section className="relative overflow-hidden border-b-2 border-black bg-ink text-white" data-testid="hero-section">
      <motion.div style={{ y: yPalm }} className="absolute -right-10 md:right-0 top-0 w-[70vw] md:w-[42vw] opacity-20 md:opacity-25 pointer-events-none select-none">
        <img src={PALM} alt="" className="w-full grayscale invert contrast-125" />
      </motion.div>
      <motion.div style={{ y: yText }} className="relative px-5 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="kicker mb-6"
          data-testid="hero-kicker"
        >
          <span className="bg-lime text-black px-2 py-1">AntCrawler</span>
          <span className="ml-3 text-neutral-300">Full-service marketing — Fort Pierce, Florida</span>
        </motion.p>
        <h1 className="font-display uppercase text-[15vw] md:text-[11vw] leading-[0.85] tracking-tight" data-testid="hero-heading">
          <MaskedLine delay={0.15}>We build</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="bg-lime text-black px-2 box-decoration-clone">colonies</span> of
          </MaskedLine>
          <MaskedLine delay={0.45}>customers.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg font-medium leading-relaxed text-neutral-300"
          data-testid="hero-subtext"
        >
          SEO, social media, paid ads, web design, and lead generation — one relentless crew
          turning local businesses into brands the mainland envies. (Marketing, not pest control.)
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button onClick={openBooking} data-testid="hero-book-button" className="btn-brutal-lime">
            Book a Consultation <ArrowUpRight size={16} />
          </button>
          <Link to="/contact" data-testid="hero-audit-button" className="btn-brutal-white-dark">
            Get a Free Marketing Audit
          </Link>
          <a href="tel:+17723324038" data-testid="hero-call-button" className="btn-brutal-ghost">
            <Phone size={16} /> 772-332-4038
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

const ServicesBento = () => (
  <section className="px-5 md:px-10 py-20 md:py-28 border-b-2 border-black" data-testid="services-overview-section">
    <FadeUp>
      <p className="kicker mb-4">What the colony does</p>
      <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] max-w-3xl" data-testid="services-overview-heading">
        Six legs of growth. Zero dead weight.
      </h2>
    </FadeUp>
    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
      {services.map((s, i) => (
        <FadeUp key={s.title} delay={i * 0.07} className={s.span}>
          <Link
            to="/services"
            data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]/g, "")}`}
            className={`card-brutal group block h-full p-7 md:p-9 shadow-[6px_6px_0_0_#0A0A0A] hover:shadow-[10px_10px_0_0_#CCFF00] hover:-translate-x-1 hover:-translate-y-1 ${
              s.lime ? "bg-lime" : ""
            }`}
          >
            <div className="flex items-start justify-between">
              <s.icon size={30} strokeWidth={2.2} />
              <ArrowUpRight size={22} className="transition-transform duration-200 group-hover:rotate-45" />
            </div>
            <h3 className="font-display text-3xl md:text-4xl uppercase mt-8">{s.title}</h3>
            <p className="mt-3 font-medium text-sm md:text-base leading-relaxed max-w-md">{s.text}</p>
          </Link>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="px-5 md:px-10 py-20 md:py-28 border-b-2 border-black bg-ink text-white" data-testid="manifesto-section">
    <FadeUp>
      <p className="kicker mb-4 text-lime">The AntCrawler manifesto</p>
      <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9]" data-testid="manifesto-heading">
        Four rules of the colony
      </h2>
    </FadeUp>
    <div className="mt-14 border-t-2 border-white/25">
      {chapters.map((c, i) => (
        <FadeUp key={c.num} delay={i * 0.05}>
          <div
            className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline py-10 border-b-2 border-white/25 transition-colors duration-300 hover:bg-lime hover:text-black px-2 md:px-4"
            data-testid={`manifesto-chapter-${c.num}`}
          >
            <span className="font-display text-6xl md:text-8xl md:col-span-3 text-white/15 group-hover:text-black transition-colors duration-300">
              {c.num}
            </span>
            <h3 className="font-display text-3xl md:text-5xl uppercase md:col-span-4">{c.title}</h3>
            <p className="font-medium text-sm md:text-base leading-relaxed md:col-span-5 text-neutral-300 group-hover:text-black transition-colors duration-300">{c.text}</p>
          </div>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Benefits = () => (
  <section className="px-5 md:px-10 py-20 md:py-28 border-b-2 border-black" data-testid="benefits-section">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
      <div>
        <FadeUp>
          <p className="kicker mb-4">Why brands march with us</p>
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9]" data-testid="benefits-heading">
            Built for businesses that hate being invisible
          </h2>
          <p className="mt-6 max-w-lg font-medium text-base md:text-lg leading-relaxed">
            You don't need another agency deck. You need phones ringing, carts filling, and a
            pipeline your competitors can't figure out. That's the whole job.
          </p>
        </FadeUp>
        <div className="mt-10 grid grid-cols-2 gap-5">
          {stats.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.06}>
              <div className="card-brutal p-6 shadow-[5px_5px_0_0_#CCFF00]" data-testid={`stat-${i}`}>
                <p className="font-display text-4xl md:text-5xl">{s.value}</p>
                <p className="mt-2 font-mono text-xs uppercase tracking-widest">{s.label}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          * Sample figures — swap in your real numbers.
        </p>
      </div>
      <FadeUp delay={0.15}>
        <DrawBorder>
          <div className="border-2 border-black shadow-[10px_10px_0_0_#0A0A0A] overflow-hidden">
            <img src={OFFICE} alt="The AntCrawler crew at work" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-500" />
          </div>
        </DrawBorder>
        <p className="mt-4 font-mono text-xs uppercase tracking-widest flex items-center gap-2">
          <span className="w-2.5 h-2.5 bg-lime border border-black inline-block" /> The crew, mid-campaign
        </p>
      </FadeUp>
    </div>
  </section>
);

const DarkCta = () => (
  <section
    className="relative px-5 md:px-10 py-24 md:py-32 bg-ink text-white border-b-2 border-black overflow-hidden"
    style={{
      backgroundImage: `linear-gradient(rgba(10,10,10,0.9), rgba(10,10,10,0.95)), url(${SAND})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
    data-testid="dark-cta-section"
  >
    <FadeUp>
      <p className="kicker text-lime mb-6">Black sand standards</p>
      <h2 className="font-display text-[11vw] md:text-[7vw] uppercase leading-[0.9]" data-testid="dark-cta-heading">
        Your competitors<br />are already <span className="text-lime">marching.</span>
      </h2>
      <p className="mt-8 max-w-xl font-medium text-base md:text-lg text-neutral-300 leading-relaxed">
        Get a free, no-fluff marketing audit: what's broken, what's leaking money, and the exact
        trails we'd crawl first.
      </p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/contact" data-testid="dark-cta-audit-button" className="btn-brutal-lime">
          Request the Free Audit <ArrowUpRight size={16} />
        </Link>
        <button onClick={openBooking} data-testid="dark-cta-book-button" className="btn-brutal-white">
          Book a Consultation
        </button>
      </div>
    </FadeUp>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <MarqueeBand items={["SEO", "Social Media", "Paid Media", "Web Design", "Lead Generation", "Fort Pierce, FL"]} />
      <ServicesBento />
      <Manifesto />
      <Benefits />
      <DarkCta />
    </>
  );
}
